import { Activity, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Link, useNavigate, useParams } from 'react-router-dom'

import AddLinkIcon from '@mui/icons-material/AddLink'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SelectAnalysisChainsModal from './SelectAnalysisChainsModal'
import fetchWithAuth from '../../utilities/fetchWithAuth'
import { usePageTitle } from '../../context/PageTitleContext'

const EditDevicePage = () => {
  const { uuid } = useParams()
  const navigate = useNavigate()
  const { setBreadcrumbs } = usePageTitle()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  const [chainsModalOpen, setChainsModalOpen] = useState(false)

  const [device, setDevice] = useState({
    name: '',
    ip: '',
    organizationId: '',
  })

  const [organizations, setOrganizations] = useState([])
  const [allAnalysisChains, setAllAnalysisChains] = useState([])
  const [selectedChains, setSelectedChains] = useState([])

  const [errors, setErrors] = useState({})

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        const [deviceRes, orgsRes, analysisChainsRes] = await Promise.all([
          fetchWithAuth(`devices/${uuid}`),
          fetchWithAuth('organizations'),
          fetchWithAuth('analysis-chains'),
        ])

        const deviceData = deviceRes.data
        const orgsData = orgsRes.data || []
        const analysisChainsData = analysisChainsRes.data.data || []

        if (deviceData.analysisChains) {
          const selected = deviceData.analysisChains
          setSelectedChains(selected)
        }

        const formattedDevice = {
          name: deviceData.name || '',
          ip: deviceData.ip || '',
          common_name: deviceData.common_name || '',
          organizationId:
            deviceData.organization?.uuid ||
            deviceData.organizationId?.uuid ||
            '',
        }

        setDevice(formattedDevice)
        setOrganizations(orgsData)
        setAllAnalysisChains(analysisChainsData)
        setBreadcrumbs(['devices', deviceData.name, 'edit'])
      } catch (err) {
        console.error('Error fetching data:', err)
        setError('Failed to load device data. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
    return () => setBreadcrumbs([])
  }, [uuid])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setDevice((prev) => ({
      ...prev,
      [name]: value,
    }))

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      setSaving(true)
      setError(null)
      setSuccess(false)

      const payload = {
        name: device.name,
        common_name: device.common_name,
        ip: device.ip,
        organizationId: device.organizationId,
        analysisChains: selectedChains.map((chain) => chain._id),
      }

      await fetchWithAuth(`devices/${uuid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      setSuccess(true)

      setTimeout(() => {
        navigate(`/devices/${uuid}`)
      }, 500)
    } catch (err) {
      console.error('Error updating device:', err)
      setError('Failed to update device. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleChainsSave = (newChainIds) => {
    const selectedChainIds = Array.from(newChainIds.ids)

    const selected = selectedChainIds
      .map((id) => allAnalysisChains.find((chain) => chain.uuid === id))
      .filter(Boolean)

    setSelectedChains(selected)
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading device data...
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton component={Link} to={`/devices/${uuid}`} edge="start">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h1">
            Edit Device
          </Typography>
        </Stack>
      </Stack>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container size={12} spacing={3}>
          <Grid size={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                  Basic Information
                </Typography>

                <TextField
                  label="Name"
                  name="name"
                  value={device.name}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                />

                <TextField
                  label="Common Name"
                  name="common_name"
                  value={device.common_name}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  error={!!errors.common_name}
                  helperText={errors.common_name}
                  required
                />

                <FormControl
                  fullWidth
                  margin="normal"
                  error={!!errors.organizationId}
                >
                  <InputLabel id="organization-label" htmlFor="organization">
                    Organization *
                  </InputLabel>
                  <Select
                    labelId="organization-label"
                    name="organizationId"
                    value={device.organizationId}
                    onChange={handleInputChange}
                    label="Organization *"
                    inputProps={{ id: 'organization' }}
                  >
                    {organizations.data.map((org) => (
                      <MenuItem key={org.uuid} value={org.uuid}>
                        {org.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Activity mode={errors.organizationId ? 'visible' : 'hidden'}>
                    <FormHelperText>{errors.organizationId}</FormHelperText>
                  </Activity>
                </FormControl>

                <TextField
                  label="IP Address"
                  name="ip"
                  value={device.ip || ''}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                />
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mt: 3 }}>
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6" component="h2">
                    Analysis Chains
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddLinkIcon />}
                    onClick={() => setChainsModalOpen(true)}
                    size="small"
                  >
                    Select Chains
                  </Button>
                </Stack>

                {selectedChains && selectedChains.length > 0 ? (
                  <Box spacing={1}>
                    {selectedChains.map((chain) => (
                      <Chip
                        key={chain._id}
                        label={chain.name || chain._id}
                        onDelete={() =>
                          setSelectedChains(
                            selectedChains.filter((c) => c.uuid !== chain.uuid),
                          )
                        }
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography color="text.secondary">
                    No analysis chains selected.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={saving}
            startIcon={
              <Activity mode={saving ? 'visible' : 'hidden'}>
                <CircularProgress size={20} color="inherit" />
              </Activity>
            }
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button
            variant="outlined"
            component={Link}
            to={`/devices/${uuid}`}
            disabled={saving}
          >
            Cancel
          </Button>
        </Box>
      </Box>
      <SelectAnalysisChainsModal
        open={chainsModalOpen}
        onClose={() => setChainsModalOpen(false)}
        onSave={handleChainsSave}
        selectedChains={selectedChains}
      />
    </Box>
  )
}

export default EditDevicePage
